export const CHECKOUT_URL = "https://menodecision-en.impultienda.ar/checkout/";

export const images = {
  hero: "https://cdn.magicpatterns.com/uploads/nxtcKGUQz7YAc87mRDvx2G/MENODECISION-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/3H4fN8QCBMYSLwmTgYrz2r/Dra_Karito_real.png",
};

export const beforeItems = [
  "You spend valuable time searching through different guidelines.",
  "You struggle to organize all the relevant considerations during a consultation.",
  "You repeatedly review the same information before evaluating a patient.",
  "You find it difficult to keep your assessment process consistent.",
  "You lack a single visual reference to organize your consultation.",
  "Every complex presentation feels like starting from zero.",
];

export const afterItems = [
  "You have visual references to guide your assessment process.",
  "You can organize relevant clinical questions in a logical sequence.",
  "You can identify areas that require additional evaluation.",
  "You can document your clinical reasoning more systematically.",
  "You have practical checklists to support your consultation workflow.",
  "You can use a consistent structure while individualizing care for each patient.",
];

export const painPoints = [
  {
    icon: "flame",
    title: "A patient arrives with severe hot flashes.",
    body: "You need to organize the initial assessment, review the relevant history, and determine which clinical considerations deserve attention. But where should you begin?",
  },
  {
    icon: "clock",
    title: "You spend too much time searching through medical guidelines.",
    body: "You know the information exists, but finding and organizing the right material during a busy consultation can be frustrating.",
  },
  {
    icon: "help",
    title: "You're unsure which questions deserve priority.",
    body: "Menopause assessment involves symptoms, medical history, differential diagnoses, risk factors, and patient preferences. Keeping everything organized takes effort.",
  },
  {
    icon: "books",
    title: "You have plenty of information but lack a practical structure.",
    body: "You've read articles, reviewed guidelines, and attended educational sessions. Yet translating that information into a repeatable consultation workflow remains challenging.",
  },
  {
    icon: "message",
    title: "You want to explain clinical considerations more clearly.",
    body: "Patients have questions about symptoms, treatment options, and potential risks. You want to structure these conversations in a way that is understandable and evidence-informed.",
  },
  {
    icon: "refresh",
    title: "Every new patient requires you to reconstruct your approach.",
    body: "You know that a more organized process could help you work more consistently, but you don't want to spend hours building one yourself.",
  },
] as const;

export const volumes = [
  {
    number: "I",
    title: "The Menopause Assessment Map",
    subtitle: "Organize Your Initial Assessment Before Deciding What Comes Next",
    intro:
      "The first consultation establishes the foundation for everything that follows. This volume helps you organize the information needed to understand the patient's presentation and identify the areas requiring further assessment.",
    items: [
      "A visual overview of the initial consultation.",
      "A structured symptom assessment map.",
      "Key medical history considerations.",
      "A guide to distinguishing menopause-related symptoms from presentations requiring differential evaluation.",
      "Questions to help organize the clinical interview.",
      "A checklist for the initial assessment.",
      "A visual reference for documenting relevant findings.",
      "A practical consultation summary template.",
    ],
    outro:
      "The goal is to give you a clearer starting point rather than leaving you to reconstruct your assessment process from memory.",
    color: "#E8A27C",
  },
  {
    number: "II",
    title: "The Symptom Decision Map",
    subtitle: "Organize the Assessment of Common Menopause Symptoms",
    intro:
      "Menopause can present through a wide range of symptoms, and similar complaints may have different explanations. This volume organizes common presentations into visual references that help you structure the questions and considerations relevant to each situation.",
    items: [
      "A visual map of vasomotor symptoms.",
      "A structured reference for sleep-related complaints.",
      "A map for genitourinary symptoms.",
      "A reference for sexual health concerns.",
      "A framework for mood-related complaints.",
      "Symptom-specific history checklists.",
      "Differential assessment considerations.",
      "A visual guide to identifying presentations requiring further evaluation.",
    ],
    outro:
      "Rather than presenting every symptom as an isolated chapter, this volume organizes the assessment around the clinical questions that arise when a patient describes what she is experiencing.",
    color: "#D9748F",
  },
  {
    number: "III",
    title: "The Clinical Risk Assessment Map",
    subtitle: "Organize the Relevant Considerations Before Discussing Treatment Options",
    intro:
      "A treatment discussion requires more than identifying symptoms. Medical history, contraindications, risk factors, patient preferences, and current clinical recommendations all matter.",
    items: [
      "A medical history review checklist.",
      "A cardiovascular risk assessment reference.",
      "A thromboembolic risk consideration map.",
      "An oncology-related history checklist.",
      "A reference for contraindications and precautions.",
      "A checklist for identifying issues requiring additional assessment.",
      "A structured risk-benefit discussion template.",
      "A clinical documentation worksheet.",
    ],
    outro:
      "These resources are intended to support clinical reasoning and documentation, not replace individualized medical assessment or current professional guidelines.",
    color: "#5C8FC7",
  },
  {
    number: "IV",
    title: "The Treatment Discussion Map",
    subtitle: "Structure the Conversation About Available Management Options",
    intro:
      "Patients often arrive with questions about hormone therapy, nonhormonal treatments, benefits, risks, and alternatives. This volume helps you organize the consultation so that the discussion is clearer, more structured, and responsive to the patient's needs.",
    items: [
      "A visual framework for organizing treatment discussions.",
      "A reference for reviewing relevant treatment goals.",
      "A guide to structuring shared decision-making.",
      "A checklist for discussing benefits and risks.",
      "A patient preference assessment worksheet.",
      "A framework for documenting the agreed plan.",
      "A reference for considering nonhormonal approaches.",
      "A consultation summary template.",
    ],
    outro:
      "The purpose is not to dictate a treatment choice. It is to help you structure the conversation that supports an individualized clinical decision.",
    color: "#5FA584",
  },
  {
    number: "V",
    title: "The Follow-Up Decision Map",
    subtitle: "Organize What to Review After the Initial Consultation",
    intro:
      "Follow-up provides an opportunity to review symptom evolution, patient experience, treatment response, adverse effects, and whether additional assessment is needed.",
    items: [
      "A follow-up consultation map.",
      "A symptom evolution tracking worksheet.",
      "A treatment response review checklist.",
      "An adverse-effect documentation template.",
      "A patient adherence discussion guide.",
      "A framework for documenting changes over time.",
      "A checklist for identifying issues requiring reassessment.",
      "A follow-up consultation summary.",
    ],
    outro:
      "With a structured reference, you can organize the information collected at each visit and document the next steps more consistently.",
    color: "#9A7FC8",
  },
  {
    number: "VI",
    title: "The Clinical Consultation Toolkit",
    subtitle: "Bring Your Decision Maps Into Your Everyday Workflow",
    intro:
      "This final volume brings the collection together into a practical set of resources designed for consultation preparation, documentation, and patient communication.",
    items: [
      "A master consultation checklist.",
      "A first-visit preparation sheet.",
      "A structured clinical history template.",
      "A symptom assessment worksheet.",
      "A clinical reasoning documentation template.",
      "A shared decision-making worksheet.",
      "A patient education planning sheet.",
      "A follow-up checklist.",
      "A clinical resource index.",
      "A quick-reference guide for using the complete system.",
    ],
    outro:
      "The objective is simple: help you bring the different parts of your menopause assessment process into one organized collection.",
    color: "#4F7FB8",
  },
];

export const collectionItems = [
  { icon: "map", title: "Visual Decision Maps", body: "Structured visual references to help organize common assessment questions and consultation pathways." },
  { icon: "check", title: "Clinical Checklists", body: "Practical lists to support a more consistent consultation and documentation workflow." },
  { icon: "file", title: "Assessment Worksheets", body: "Templates for organizing symptoms, medical history, clinical considerations, and follow-up information." },
  { icon: "book", title: "Consultation References", body: "Quick-reference resources for preparing and structuring patient conversations." },
  { icon: "message", title: "Patient Communication Tools", body: "Worksheets and frameworks to support clear, individualized, evidence-informed discussions." },
  { icon: "index", title: "A Master Reference Index", body: "An organized starting point for finding the relevant resource within the collection." },
] as const;

export const bonuses = [
  {
    number: 1,
    title: "The Menopause Casebook",
    subtitle: "A Practical Collection of Clinical Scenarios",
    description:
      "Explore simulated menopause-related clinical scenarios and practice organizing the information needed to evaluate each presentation.",
    items: [
      "Illustrative patient scenarios.",
      "Clinical history summaries.",
      "Questions to guide assessment.",
      "Differential considerations.",
      "Discussion prompts.",
      "Suggested reasoning frameworks.",
      "References for further reading.",
    ],
    note: "Use these scenarios for educational purposes and to reflect on how different clinical factors influence assessment.",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/jF7cSou5NPqNNNLsjH82en/BONUS_1_MENODECISION-EN.png",
  },
  {
    number: 2,
    title: "The 5-Minute Consultation Preparation Checklist",
    subtitle: "Organize the Key Questions Before You Begin",
    description:
      "A concise checklist designed to help you prepare for a menopause consultation and identify the information that may be relevant to the assessment.",
    items: [
      "A pre-consultation checklist.",
      "Key history prompts.",
      "Symptom assessment reminders.",
      "Risk consideration prompts.",
      "Documentation reminders.",
      "A consultation summary template.",
    ],
    note: "The checklist is a preparation aid, not a substitute for a complete clinical evaluation.",
    value: 19,
    image: "https://cdn.magicpatterns.com/uploads/n1Su4JxeVgshE8yXQ46jVW/BONUS_2_MENODECISION-EN.png",
  },
  {
    number: 3,
    title: "The Menopause Symptom Atlas",
    subtitle: "A Visual Reference for Common Menopause-Related Complaints",
    description:
      "A visual collection designed to help organize the assessment of common symptoms and identify questions that may require further evaluation.",
    items: [
      "Symptom category reference sheets.",
      "Visual symptom summaries.",
      "Clinical interview prompts.",
      "Differential assessment considerations.",
      "Documentation reminders.",
      "A quick-reference index.",
    ],
    note: "",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/gByQizfsZr6TUeBs9arbJX/BONUS_3_MENODECISION-EN.png",
  },
  {
    number: 4,
    title: "The Patient Conversation Playbook",
    subtitle: "Structure Difficult Conversations With Greater Clarity",
    description:
      "A collection of conversation frameworks to help you explain menopause-related topics in understandable, patient-centered language.",
    items: [
      "A framework for explaining menopause.",
      "Prompts for discussing hormone therapy.",
      "A structure for communicating benefits and risks.",
      "Questions to explore patient concerns.",
      "Shared decision-making conversation guides.",
      "Responses to common patient questions.",
      "A framework for checking patient understanding.",
    ],
    note: "The resources support individualized communication rather than providing universal answers to every clinical situation.",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/anc2p76NtMahraPEqS7uqZ/BONUS_4_MENODECISION-EN.png",
  },
  {
    number: 5,
    title: "The Menopause Consultation Checklist Library",
    subtitle: "A Collection of Practical Checklists for Your Clinical Workflow",
    description: "Use these checklists as adaptable references throughout the consultation process.",
    items: [
      "Initial consultation checklist.",
      "Symptom assessment checklist.",
      "Medical history review checklist.",
      "Treatment discussion checklist.",
      "Documentation checklist.",
      "Follow-up consultation checklist.",
      "Patient education checklist.",
      "Clinical resource review checklist.",
    ],
    note: "Adapt the templates to your specialty, local requirements, and clinical setting.",
    value: 19,
    image: "https://cdn.magicpatterns.com/uploads/7SJ8D2d1HJcoSW5EXj6EL1/BONUS_5_MENODECISION-EN.png",
  },
];

export const testimonials = [
  {
    name: "Dr. Elena Rodríguez",
    role: "Family Physician · Texas, USA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/64ba8209-439f-40d0-a2e7-395eda5cfde7.jpg",
    quote:
      "I keep Volume I open during first visits. Having the assessment laid out visually means I'm no longer jumping between three different guidelines while the patient waits.",
  },
  {
    name: "Dr. Michael Harris",
    role: "General Practitioner · Manchester, UK",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/418e46be-e83c-438c-b7b6-8bfc4347e11c.jpg",
    quote:
      "The risk assessment map is what sold me. It gives me a consistent order for history, contraindications and documentation before I even open the treatment conversation.",
  },
  {
    name: "Dr. Amara Okafor",
    role: "OB-GYN · Toronto, Canada",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/18eb9380-7147-43c0-80ff-061c7051d520.jpg",
    quote:
      "The Patient Conversation Playbook changed how I explain benefits and risks. Patients leave understanding their options, and my notes are far more structured.",
  },
  {
    name: "Dr. Susan Whitaker",
    role: "Internal Medicine · Sydney, Australia",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/3e76b65f-07d5-4548-9838-c4633840b295.jpg",
    quote:
      "For $27 I expected a PDF. Instead I got a full workflow I adapted to my clinic in one afternoon. The follow-up worksheets alone saved me hours.",
  },
];

export const purchaseNotifications = [
  { name: "Dr. María Fernández", country: "Spain", flag: "🇪🇸" },
  { name: "Dr. Carlos Gutiérrez", country: "Mexico", flag: "🇲🇽" },
  { name: "Dr. Valentina Rojas", country: "Colombia", flag: "🇨🇴" },
  { name: "Dr. Lucía Martínez", country: "Argentina", flag: "🇦🇷" },
  { name: "Dr. Andrés Morales", country: "Chile", flag: "🇨🇱" },
  { name: "Dr. Camila Torres", country: "Peru", flag: "🇵🇪" },
  { name: "Dr. Sofía Ramírez", country: "United States", flag: "🇺🇸" },
  { name: "Dr. Daniela Castro", country: "Ecuador", flag: "🇪🇨" },
  { name: "Dr. Gabriela Herrera", country: "Costa Rica", flag: "🇨🇷" },
  { name: "Dr. Isabel Navarro", country: "Uruguay", flag: "🇺🇾" },
  { name: "Dr. Ricardo Peña", country: "Dominican Republic", flag: "🇩🇴" },
  { name: "Dr. Paula Méndez", country: "Puerto Rico", flag: "🇵🇷" },
  { name: "Dr. Natalia Vargas", country: "Panama", flag: "🇵🇦" },
  { name: "Dr. Javier Ortega", country: "Guatemala", flag: "🇬🇹" },
];

export const faqs = [
  {
    q: "How will I receive Menopause Decision Map™?",
    a: "After your payment is confirmed, you'll receive instructions for accessing your digital collection. The six volumes and five bonuses are provided in downloadable digital formats.",
  },
  {
    q: "Is this suitable for physicians in different countries?",
    a: "The collection is designed as an educational reference for healthcare professionals. However, clinical recommendations, prescribing requirements, documentation standards, and professional guidelines can differ by country. You should verify the applicability of the material against current local guidance and professional requirements.",
  },
  {
    q: "Do I need to be a menopause specialist?",
    a: "The collection is intended for qualified healthcare professionals involved in women's health or menopause-related care. Your professional scope, training, and local regulations determine which clinical activities you may perform.",
  },
  {
    q: "Does the system make diagnoses or prescribe treatments for me?",
    a: "No. Menopause Decision Map™ is an educational and organizational resource. It does not replace a comprehensive clinical evaluation, professional judgment, current evidence-based guidelines, or individualized treatment decisions.",
  },
  {
    q: "Is the collection downloadable?",
    a: "Yes. The product is delivered digitally, allowing you to download the included materials and consult your saved files when needed.",
  },
  {
    q: "Is this a one-time payment?",
    a: "Yes. The advertised price is $27 USD as a one-time payment, with no recurring subscription fees.",
  },
  {
    q: "Will I have access to the materials after purchasing?",
    a: "Yes. Your purchase includes ongoing access to the files provided with your order, subject to the applicable delivery and purchase terms.",
  },
  {
    q: "Is the content updated?",
    a: "Clinical evidence and professional recommendations evolve over time. Check the edition date and references provided with the material, and verify important clinical decisions against current authoritative guidance.",
  },
  {
    q: "What if the product isn't right for me?",
    a: "Your purchase includes a 7-day money-back guarantee. Contact us within the stated period and follow the refund instructions provided with your order.",
  },
  {
    q: "Is the content intended for patients?",
    a: "The main collection is designed for healthcare professionals. Some accompanying resources may support patient education, but they should be reviewed and adapted before being used in your practice.",
  },
];
